import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError, EMPTY} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {SwitchBoard} from '../services/SwitchBoard';
import {Session} from '../services/Session';

@Injectable({
  providedIn: 'root',
})
export class RequestInterceptor implements HttpInterceptor {
  private isLoggingOut = false;

  constructor(
    private switchBoard: SwitchBoard
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isPublicEndpoint(request.url)) {
      return next.handle(request);
    }

    if (Session.isSessionExpired()) {
      return this.handleAuthError();
    }

    Session.resetSessionStart();

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isAuthError(error)) {
          return this.handleAuthError();
        }
        return throwError(() => error);
      })
    );
  }

  private isPublicEndpoint(url: string): boolean {
    const publicSegments = [
      'login',
      'resetpassword',
      'forgotpassword',
      'signup',
      'logos',
      'createpassword',
      'token/refresh',
      'handoff',
      'create-new-firm',
      // external taxpayer questionnaire form — no session; the token in the
      // path is the credential (CU-868kg6qux)
      'questionnaire-form',
    ];

    const pathname = this.extractPathname(url).toLowerCase();
    return publicSegments.some(seg => {
      const escaped = seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|/)${escaped}(/|$)`).test(pathname);
    });
  }

  private extractPathname(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url.split('?')[0];
    }
  }

  private isAuthError(error: HttpErrorResponse): boolean {
    if (error.status === 401 || error.status === 403) {
      return true;
    }

    if (error.status === 412 && this.isTokenRelatedError(error)) {
      return true;
    }

    return false;
  }

  private isTokenRelatedError(error: HttpErrorResponse): boolean {
    const body = error.error;
    if (body && typeof body === 'object' && body.message) {
      const msg = body.message.toLowerCase();
      return msg.includes('invalid token') ||
        msg.includes('blank token') ||
        msg.includes('invalid claims') ||
        msg.includes('invalid refresh token');
    }
    if (typeof body === 'string') {
      const msg = body.toLowerCase();
      return msg.includes('invalid token') ||
        msg.includes('blank token') ||
        msg.includes('invalid claims') ||
        msg.includes('invalid refresh token');
    }
    return false;
  }

  private handleAuthError(): Observable<never> {
    if (this.isLoggingOut) {
      return EMPTY;
    }

    this.isLoggingOut = true;

    this.switchBoard.onNewToast.next({
      toastId: Math.floor(Math.random() * 1000),
      type: 'info',
      message: 'Your session has expired. Redirecting to login...',
      toastClass: 'toast-info',
      duration: 2000
    });

    setTimeout(() => {
      this.switchBoard.onLogOut.next({});
      setTimeout(() => { this.isLoggingOut = false; }, 1000);
    }, 1500);

    return EMPTY;
  }
}
